import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { XSmallSecondaryButton } from '../Button'
import { MOBILE } from '../../guided-toolbox/utils/breakpoints'

const cardBgColor = p => p.theme.cardBgColor ?? '#ffffff'

// Adds translucency only when the browser supports blur; otherwise, keeps the card solid.
export const translucentCardBackground = p => css`
  @supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
    background-color: color-mix(in srgb, ${cardBgColor(p)} 15%, transparent);
    backdrop-filter: blur(7px);
    -webkit-backdrop-filter: blur(7px);
  }

  /* No background art on mobile, so keep the card solid. */
  ${MOBILE} {
    background-color: ${cardBgColor(p)};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`

// Shared card background, border, and radius; each card defines its own padding.
export const roundedCardBase = p => css`
  box-sizing: border-box;
  background-color: ${cardBgColor(p)};
  border: 2px solid ${p.theme.primaryLighter ?? '#d4ebf2'};
  border-radius: 10px;

  ${translucentCardBackground(p)}
`

export const roundedCardHeading = p => css`
  margin: 0;
  color: ${p.theme.lightestGray ?? '#7a8e96'};
  font-size: 23px;
  font-weight: 400;
  line-height: 24px;
`

export const CardStyled = styled.div`
  ${roundedCardBase};
  width: 100%;
  max-width: 900px;
  padding: 10px 20px 16px;

  ${MOBILE} {
    /* Stack the card contents so the Edit button can be placed below the body. */
    display: flex;
    flex-direction: column;
    padding: 20px;
  }
`

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: 10px;

  /* Preserve the header height when the Edit button is absent. */
  min-height: 44px;

  ${MOBILE} {
    ${p =>
      p.$collapseOnMobile
        ? css`
            /* Remove the header wrapper from the layout, allowing the Edit button to move below the card body. */
            display: contents;
          `
        : css`
            min-height: 0;
            padding-bottom: 20px;
          `}
  }
`

export const HeaderContent = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4px 20px;
  min-width: 0;

  ${MOBILE} {
    ${p =>
      p.$collapseOnMobile &&
      css`
        /* Remove this nested wrapper so its children also participate in the card's flex layout. */
        display: contents;
      `}
  }
`

export const Heading = styled.h2`
  ${roundedCardHeading};

  ${MOBILE} {
    ${p =>
      p.$collapseOnMobile &&
      css`
        margin-bottom: 20px;
      `}
  }
`

const headerSlot = css`
  display: flex;
  align-items: center;
  min-height: 30px;
  margin-top: 10px;
`

export const HeaderAside = styled.div`
  ${headerSlot};
  min-width: 0;

  ${MOBILE} {
    width: 100%;
    margin-top: 8px;
  }
`

export const HeaderAction = styled.div`
  ${headerSlot};
  flex: none;
  margin-left: 10px;
`

export const EditPill = styled(XSmallSecondaryButton)`
  font-weight: 400;
  align-self: flex-start;
  margin-top: 10px;
  margin-left: 20px;

  ${MOBILE} {
    ${p =>
      p.$collapseOnMobile
        ? css`
            /* Place the Edit button after the card body and stretch it to full width. */
            order: 1;
            align-self: stretch;
            margin-top: 20px;
            margin-left: 0;
          `
        : css`
            margin-top: 0;
          `}
  }
`
